import { z } from "zod";
import {
  assertNoObsoleteSubmission,
  discardObsoleteSubmissions,
} from "./obsolete-submissions";

const uuid = z.uuid().toLowerCase();
const identity = {
  version: z.literal(2),
  uploadKey: uuid,
  visitKey: uuid,
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
};
const schema = z.discriminatedUnion("kind", [
  z.strictObject({ ...identity, kind: z.literal("intent") }),
  z.strictObject({ ...identity, kind: z.literal("upload"), uploadId: uuid }),
  z.strictObject({ ...identity, kind: z.literal("accepting"), uploadId: uuid }),
  z.strictObject({
    ...identity,
    kind: z.literal("accepted"),
    uploadId: uuid,
    visitPublicId: z.uuidv4().toLowerCase(),
  }),
]);
export type AudioVisitSubmission = z.output<typeof schema>;
type Intent = Extract<AudioVisitSubmission, { kind: "intent" }>;
type Upload = Extract<AudioVisitSubmission, { kind: "upload" }>;
type Accepting = Extract<AudioVisitSubmission, { kind: "accepting" }>;
type Accepted = Extract<AudioVisitSubmission, { kind: "accepted" }>;
const storageKey = (account: string): string =>
  `rosc:audio-visit-submission:${encodeURIComponent(account)}`;
export const readAudioVisitSubmission = (
  account: string,
): AudioVisitSubmission | null => {
  assertNoObsoleteSubmission(account, "rosc:audio-visit-submission:");
  try {
    const value = sessionStorage.getItem(storageKey(account));
    return value === null ? null : schema.parse(JSON.parse(value));
  } catch {
    throw Error(
      "Saved audio submission cannot be read. Check Visits before trying again.",
    );
  }
};
const save = (account: string, value: AudioVisitSubmission): void => {
  try {
    sessionStorage.setItem(
      storageKey(account),
      JSON.stringify(schema.parse(value)),
    );
  } catch {
    throw Error(
      "Retry identity could not be saved. Check Visits before trying again.",
    );
  }
};
const transition = (
  account: string,
  expected: AudioVisitSubmission,
  next: AudioVisitSubmission,
): void => {
  const current = readAudioVisitSubmission(account);
  if (
    !current ||
    current.visitKey !== expected.visitKey ||
    current.uploadKey !== expected.uploadKey ||
    current.fingerprint !== expected.fingerprint ||
    current.kind !== expected.kind ||
    ("uploadId" in expected &&
      (!("uploadId" in current) || current.uploadId !== expected.uploadId))
  )
    throw Error(
      "This audio submission is no longer active. Check Visits before trying again.",
    );
  save(account, next);
};
export const prepareAudioVisitSubmission = (
  account: string,
  fingerprint: string,
): AudioVisitSubmission => {
  const existing = readAudioVisitSubmission(account);
  if (existing) {
    if (existing.fingerprint !== fingerprint)
      throw Error(
        "This audio submission has changed. Reselect the same file/settings or check Visits before abandoning it.",
      );
    return existing;
  }
  const pending: Intent = {
    version: 2,
    kind: "intent",
    uploadKey: crypto.randomUUID(),
    visitKey: crypto.randomUUID(),
    fingerprint,
  };
  save(account, pending);
  return pending;
};
export const rememberAudioUpload = (
  account: string,
  expected: Intent,
  id: string,
): Upload => {
  const next: Upload = {
    ...expected,
    version: 2,
    kind: "upload",
    uploadId: uuid.parse(id),
  };
  transition(account, expected, next);
  return next;
};
export const beginAudioVisitAcceptance = (
  account: string,
  expected: Upload,
): Accepting => {
  const next: Accepting = { ...expected, version: 2, kind: "accepting" };
  transition(account, expected, next);
  return next;
};
export const completeAudioVisitSubmission = (
  account: string,
  expected: Accepting,
  visitPublicId: string,
): Accepted => {
  const next: Accepted = {
    ...expected,
    version: 2,
    kind: "accepted",
    visitPublicId,
  };
  transition(account, expected, next);
  return next;
};
export const abandonAudioVisitSubmission = (account: string): void => {
  discardObsoleteSubmissions(account, "rosc:audio-visit-submission:");
  try {
    sessionStorage.removeItem(storageKey(account));
  } catch {
    throw Error(
      "Retry identity could not be removed. Check Visits before trying again.",
    );
  }
};
export const beginAudioFileDraft = (account: string): void => {
  if (readAudioVisitSubmission(account)?.kind === "accepted")
    abandonAudioVisitSubmission(account);
};
